import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ComplexListItemTree,
  Dialog,
} from "@inductiveautomation/ws-components";

import { OrgNodeTree } from "./api/organizations.api";
import {
  useAllDescendantsDirectMembers,
  useDirectMemberOrgItems,
} from "./dialogs/directMemberTree";

interface OrganizationsLeaveDialogProps {
  testId: string;
  organizationName: string;
  structure: OrgNodeTree;
  // Org ids the current account is a direct member of, used to flag unaffected orgs.
  joinedOrgs: string[];
  onCancel: () => void;
  onSubmit: () => void;
}

const OrganizationsLeaveDialog = ({
  testId,
  organizationName,
  structure,
  joinedOrgs,
  onCancel,
  onSubmit,
}: OrganizationsLeaveDialogProps) => {
  const { t } = useTranslation();
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const items = useDirectMemberOrgItems(structure, joinedOrgs, "leave-organization");

  // When the user is a direct member of every child org, leaving revokes no inherited
  // access — show a reassuring info alert and skip the confirmation checkbox.
  const allChildrenDirect = useAllDescendantsDirectMembers(structure, joinedOrgs);

  const orgNodeCount = useMemo(() => {
    const count = (node: OrgNodeTree): number =>
      1 + node.children.reduce((sum, child) => sum + count(child), 0);
    return count(structure);
  }, [structure]);
  const hasSmallOrgTree = orgNodeCount <= 2;

  return (
    <Dialog
      testId={testId}
      isOpen={true}
      showCloseButton
      onClose={onCancel}
      maintainHeight
      fullWidthContentArea
      collapseContentScrollGutter={hasSmallOrgTree}
      headline={t("organizations.actions.leave_organization.heading")}
      supportingText={t("organizations.actions.leave_organization.description", {
        organizationName,
      })}
      alert={{
        id: "leave-organization-dialog-alert",
        hasCloseButton: false,
        closeButtonEventHandler: () => undefined,
        description: allChildrenDirect
          ? t("organizations.actions.leave_organization.alert_description_all_direct")
          : t("organizations.actions.leave_organization.alert_description"),
        variant: allChildrenDirect ? "info" : "warning",
      }}
      contentArea={<ComplexListItemTree items={items} />}
      helper={
        allChildrenDirect
          ? undefined
          : {
              onCheckboxChange: () => setHasConfirmed(!hasConfirmed),
              helperConfirmLabel: t(
                "organizations.actions.leave_organization.confirmation_text",
                { organizationName }
              ),
            }
      }
      actions={[
        {
          testId: "cancel",
          label: t("organizations.actions.cancel.label"),
          type: "cancel",
        },
        {
          testId: "submit",
          label: t("organizations.actions.leave_organization.submit_label"),
          type: "submit",
          onClick: onSubmit,
          disabled: !allChildrenDirect && !hasConfirmed,
        },
      ]}
    />
  );
};

export default OrganizationsLeaveDialog;
